/**
 * TimelinePage
 *
 * Interactive timeline view showing yearly transits (hotspots).
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks';
import { get } from '../api/client';
import { VerticalTimeline } from '../components/Timeline';
import type { Timeline, TimelineHotspot } from '../types';

import './TimelinePage.css';

export function TimelinePage() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());

  // Fetch timeline data
  const fetchTimeline = useCallback(async (targetYear: number) => {
    setLoading(true);
    setError(null);

    try {
      const data = await get<Timeline>(`/api/timeline/${targetYear}`);
      setTimeline(data);
    } catch (err) {
      const apiError = err as { status?: number; message?: string };
      if (apiError.status === 404) {
        setError('no_chart');
      } else {
        setError(apiError.message || 'Failed to load timeline');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTimeline(year);
    }
  }, [isAuthenticated, year, fetchTimeline]);

  // Handle Ask Lucy - navigate to chat with pre-filled question about the transit
  const handleAskLucy = (hotspot: TimelineHotspot) => {
    const lang = i18n.language;

    // Build a question about this transit
    const transitTitle = hotspot.title[lang as 'en' | 'sr'] || hotspot.title.en;
    const date = new Date(hotspot.date).toLocaleDateString(lang === 'sr' ? 'sr-Latn' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    const question = lang === 'sr'
      ? `Reci mi više o transitu "${transitTitle}" koji se dešava ${date}. Kako će to uticati na mene?`
      : `Tell me more about the transit "${transitTitle}" happening on ${date}. How will this affect me?`;

    // Navigate to chat with the question as a URL parameter
    navigate(`/?q=${encodeURIComponent(question)}`);
  };

  const handleYearChange = (newYear: number) => {
    setYear(newYear);
  };

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="timeline-page">
        <div className="empty-state">
          <h2>{t('auth.login')}</h2>
          <p>{t('timeline.loginRequired')}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="timeline-page">
        <div className="loading-state">
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p>{t('timeline.loading')}</p>
        </div>
      </div>
    );
  }

  // No chart error
  if (error === 'no_chart') {
    return (
      <div className="timeline-page">
        <div className="empty-state">
          <h2>&#9737;</h2>
          <p>{t('timeline.noChart')}</p>
          <button
            className="profile-action-btn"
            onClick={() => navigate('/chat')}
          >
            {t('timeline.addBirthData')}
          </button>
        </div>
      </div>
    );
  }

  // Other error
  if (error) {
    return (
      <div className="timeline-page">
        <div className="empty-state">
          <h2>{t('common.error')}</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // No timeline data
  if (!timeline) {
    return (
      <div className="timeline-page">
        <div className="empty-state">
          <h2>{t('common.error')}</h2>
          <p>No timeline data available</p>
        </div>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();

  return (
    <div className="timeline-page" data-testid="timeline-page">
      <header className="timeline-header">
        <div className="timeline-title-section">
          <h1>{t('timeline.title')}</h1>
          <p className="timeline-subtitle">{t('timeline.subtitle')}</p>
        </div>

        <div className="year-selector">
          <button
            className="year-btn"
            onClick={() => handleYearChange(year - 1)}
            disabled={year <= currentYear - 2}
          >
            &larr;
          </button>
          <span className="year-display">{year}</span>
          <button
            className="year-btn"
            onClick={() => handleYearChange(year + 1)}
            disabled={year >= currentYear + 2}
          >
            &rarr;
          </button>
        </div>
      </header>

      {/* Summary stats */}
      <div className="timeline-stats">
        <div className="stat-box">
          <span className="stat-number">{timeline.summary.total_hotspots}</span>
          <span className="stat-label">{t('timeline.totalTransits')}</span>
        </div>
        <div className="stat-box favorable">
          <span className="stat-number">{timeline.summary.favorable_count}</span>
          <span className="stat-label">{t('timeline.favorable')}</span>
        </div>
        <div className="stat-box challenging">
          <span className="stat-number">{timeline.summary.challenging_count}</span>
          <span className="stat-label">{t('timeline.challenging')}</span>
        </div>
        <div className="stat-box neutral">
          <span className="stat-number">{timeline.summary.neutral_count}</span>
          <span className="stat-label">{t('timeline.neutral')}</span>
        </div>
      </div>

      {/* Vertical Timeline */}
      <VerticalTimeline
        hotspots={timeline.hotspots}
        year={year}
        onAskLucy={handleAskLucy}
      />
    </div>
  );
}

export default TimelinePage;
